import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { InlineLoadSkeleton } from "./common/InlineLoadSkeleton";
import { projectService } from "../services/projectService";
import toast from "react-hot-toast";

const DEFAULT_NAME = "Sample Project";

const STRUCTURE_OPTIONS = [
  {
    value: "SINGLE_REPOSITORY",
    label: "Single Repository for all Cases",
  },
  {
    value: "MULTI_SUITE",
    label: "Multiple Test Suites to manage cases",
  },
];

const TEMPLATE_OPTIONS = [
  {
    value: "SINGLE_STEP",
    label: "Single Step with Single Expected Result",
  },
  {
    value: "MULTIPLE_STEPS",
    label: "Multi Step with Multi Expected Result",
  },
  { value: "BDD", label: "BDD" },
];

function projectTypeForStructure(structure) {
  if (structure === "MULTI_SUITE") {
    return "Multiple Test Suites to manage cases";
  }
  return "Single Repository for all Cases";
}

const CreateSampleProjectModal = ({
  open,
  onOpenChange,
  clientId,
  onSuccess,
}) => {
  const [name, setName] = useState(DEFAULT_NAME);
  const [description, setDescription] = useState("");
  const [structure, setStructure] = useState("SINGLE_REPOSITORY");
  const [testCaseStructureType, setTestCaseStructureType] =
    useState("MULTIPLE_STEPS");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(DEFAULT_NAME);
      setDescription("");
      setStructure("SINGLE_REPOSITORY");
      setTestCaseStructureType("MULTIPLE_STEPS");
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) {
      toast.error("No client selected. Choose a client context first.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim() || DEFAULT_NAME,
        description: description.trim() || undefined,
        client_id: clientId,
        structure,
        project_type: projectTypeForStructure(structure),
        test_case_structure_type: testCaseStructureType,
        ai_test_case_generation: false,
        test_case_approval: false,
      };
      const res = await projectService.createSampleProject(payload);
      if (!res.success) {
        throw new Error(
          res.message || res.error || "Failed to create sample project",
        );
      }
      toast.success(res.message || "Sample project created successfully");
      onOpenChange(false);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to create sample project";
      toast.error(
        typeof msg === "string" ? msg : "Failed to create sample project",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-900">
            Create Sample Project
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="sample-project-name" className="text-gray-700">
              Project Name
            </Label>
            <Input
              id="sample-project-name"
              className="mt-1.5"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={DEFAULT_NAME}
            />
          </div>
          <div>
            <Label htmlFor="sample-project-desc" className="text-gray-700">
              Description
            </Label>
            <Textarea
              id="sample-project-desc"
              className="mt-1.5 min-h-[80px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </div>
          <div>
            <Label className="text-gray-700">
              Project Structure Type <span className="text-red-500">*</span>
            </Label>
            <Select value={structure} onValueChange={setStructure}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl border border-gray-200">
                <SelectValue placeholder="Select structure" />
              </SelectTrigger>
              <SelectContent>
                {STRUCTURE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-700">
              Test Case Template Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={testCaseStructureType}
              onValueChange={setTestCaseStructureType}
            >
              <SelectTrigger className="mt-1.5 h-11 rounded-xl border border-gray-200">
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <InlineLoadSkeleton className="h-4 w-4 mr-2 rounded-full bg-white/30" />
                  Creating…
                </>
              ) : (
                "Create Sample Project"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSampleProjectModal;
